import { Card } from '@mui/material';
import { CustomChipGroup } from '../Chip/CustomChipGroup';
import React, { useMemo, useState } from 'react';
import { FeedSubresource, IFeedFilter } from '../../redux/activity/types';

export interface ICategorySelector {
    tags: string[];
    clickable?: boolean; // Prop to control if tags are clickable
    dynamicFilter?: IFeedFilter[];
    loading?: boolean;
    onClickFilter?: (tag: string) => void;
}

export const CategorySelector: React.FC<ICategorySelector> = ({
    tags,
    clickable = false,
    onClickFilter,
    dynamicFilter,
    loading,
}) => {
    const [selectedTag, setSelectedTag] = useState<string | null>(tags[0]); // State for single selection

    // Handle tag selection (only one tag at a time)
    const handleTagClick = (tag: string) => {
        // If the tag is already selected, deselect it, otherwise select it
        setSelectedTag(prev => (prev === tag ? null : tag));
        const getDynamicResource = dynamicFilter?.find(d => d.name === tag);
        if (getDynamicResource) {
            onClickFilter && onClickFilter(getDynamicResource.subresource);
        } else {
            onClickFilter && onClickFilter(tag as FeedSubresource);
        }
    };

    const dynamicTags = useMemo(() => {
        if (dynamicFilter) {
            return dynamicFilter.map(d => d.name);
        }
        return tags;
    }, [dynamicFilter, tags]);

    return (
        <Card
            id="feed-filter"
            sx={{
                width: '100%',
                borderRadius: 0,
                overflow: 'auto', // Ensure horizontal scroll works
                boxShadow: 'none',
                my: 2,
                backgroundColor: 'transparent',
                position: 'relative',
            }}>
            <CustomChipGroup
                tags={dynamicTags}
                loading={loading}
                maxVisible={10}
                clickable={clickable}
                withNiceColor={false}
                tagSx={{
                    fontSize: 12,
                    p: 2,
                    cursor: clickable ? 'pointer' : 'default', // Make tags clickable if the prop is true
                    ...(selectedTag && {
                        // Optional: Add additional styles if any tag is selected
                        boxShadow: '0 0 10px rgba(0, 0, 0, 0.1)',
                    }),
                }}
                onTagClick={clickable ? handleTagClick : () => {}} // Only make tags clickable if the `clickable` prop is true
            />
        </Card>
    );
};
